/** @format */
import { useContext, useEffect, useState } from 'react';

import { Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { EventContext } from '../../Contexts/EventContext';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import Loading from '../../Shared/Components/Loading';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { serverErrorHandler } from '../../Shared/utils/helper';

const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .required('لطفا نام را وارد نمایید')
    .max(100, 'نام نمی‌تواند بیشتر از 100 کاراکتر باشد'),
  last_name: Yup.string()
    .required('لطفا نام خانوادگی را وارد نمایید')
    .max(100, 'نام خانوادگی نمی‌تواند بیشتر از 100 کاراکتر باشد'),
  mobile: Yup.string()
    .required('لطفا شماره موبایل را وارد نمایید')
    .min(11, 'شماره موبایل باید 11 کاراکتر باشد')
    .max(11, 'شماره موبایل باید 11 کاراکتر باشد')
    .matches(/^09\d{9}$/, 'شماره موبایل باید با 09 شروع شود و 11 رقم باشد'),
  province_id: Yup.number('لطفا استان را انتخاب نمایید').required(
    'لطفا استان را انتخاب نمایید',
  ),
  gender: Yup.number('لطفا جنسیت را انتخاب نمایید').required(
    'لطفا جنسیت را انتخاب نمایید',
  ),
  nat_code: Yup.string()
    .required('لطفا کد ملی را وارد نمایید')
    .length(10, 'کد ملی باید دقیقاً 10 کاراکتر باشد'),
});

const Register = () => {
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const eventContext = useContext(EventContext);
  const [serverErrors, setServerErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getProvinces();
  }, []);

  const getProvinces = async () => {
    setLoading(true);
    await useApi.get(
      '/province',
      {},
      (res) => {
        eventContext.provinces = res.data.data;
        setLoading(false);
      },
      (err) => {
        error('خطا در دریافت استان‌ها');
        setLoading(false);
      },
    );
  };

  const onSubmitLoginForm = async (values, { setSubmitting }) => {
    setSubmitting(true);
    setFormLoading(true);

    await useApi.post(
      '/auth/register',
      values,
      (res) => {
        success('ثبت نام با موفقیت انجام شد.');
        setFormLoading(false);
        setSubmitting(false);
        navigate('/register/password', {
          state: {
            nat_code: values.nat_code,
            register_token: res?.data?.data?.register_token,
          },
        });
        return;
      },
      (err) => {
        if (err?.status === 422) {
          serverErrorHandler(err?.data?.errors, setServerErrors);
          setFormLoading(false);
          setSubmitting(false);
        } else {
          error('خطای ناخواسته‌ای رخ داده است');
          setFormLoading(false);
          setSubmitting(false);
        }
      },
    );

    setSubmitting(false);
    setLoading(false);
  };

  return loading ? (
    <Loading />
  ) : (
    <div className='w-100 d-flex flex-column justify-content-center align-items-center bg-purple vh-100'>
      <section className='container'>
        <Formik
          initialValues={{
            first_name: '',
            last_name: '',
            mobile: '',
            province_id: '',
            gender: '',
            nat_code: '',
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmitLoginForm}
        >
          {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className='row'>
                {/* لوگو و توضیحات */}
                <div className='col-12 d-flex flex-column p-2 text-center'>
                  <img
                    className='w-15 mx-auto'
                    src={eventContext?.setting?.logo?.logo}
                    alt={eventContext?.setting?.name}
                  />
                  <h3 className='text-dark-mode f-14 my-3'>
                    برای ثبت نام در {eventContext?.setting?.name ?? ''} لطفا فرم
                    زیر را پر کنید
                  </h3>
                  <div className='d-flex flex-row my-3 align-items-center'>
                    <hr className='w-30 bg-hr' />
                    <span className='text-dark-mode f-14 mx-2'>
                      ثبت نام در {eventContext?.setting?.name ?? ''}
                    </span>
                    <hr className='w-30 bg-hr' />
                  </div>
                </div>

                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      نام
                    </label>
                    <input
                      type='text'
                      name='first_name'
                      value={values.first_name}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage
                      error={
                        errors?.first_name
                          ? errors?.first_name
                          : serverErrors?.first_name
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>

                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      نام خانوادگی
                    </label>
                    <input
                      type='text'
                      name='last_name'
                      value={values.last_name}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage
                      error={
                        errors?.last_name
                          ? errors?.last_name
                          : serverErrors?.last_name
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>

                {/* کد ملی */}
                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      کد ملی
                    </label>
                    <input
                      type='text'
                      name='nat_code'
                      value={values.nat_code}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage
                      error={
                        errors?.nat_code
                          ? errors?.nat_code
                          : serverErrors?.nat_code
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>

                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      موبایل
                    </label>
                    <input
                      type='text'
                      name='mobile'
                      value={values.mobile}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage
                      error={
                        errors?.mobile ? errors?.mobile : serverErrors?.mobile
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      استان
                    </label>

                    <select
                      name='province_id'
                      value={values.province_id}
                      onChange={handleChange}
                      className='form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    >
                      <option value=''>انتخاب استان</option>
                      {eventContext.provinces
                        .filter(
                          (p) => p?.name !== 'سراسری' && p?.name !== 'فدراسیون',
                        )
                        .map((province) => (
                          <option key={province.id} value={province.id}>
                            {province.name}
                          </option>
                        ))}
                    </select>
                    <ErrorMessage
                      error={
                        errors?.province_id
                          ? errors?.province_id
                          : serverErrors?.province_id
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      جنسیت
                    </label>

                    <select
                      name='gender'
                      value={values.gender}
                      onChange={handleChange}
                      className='form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    >
                      <option value=''>انتخاب جنسیت</option>
                      <option value='1'>مرد</option>
                      <option value='0'>زن</option>
                    </select>
                    <ErrorMessage
                      error={
                        errors?.gender ? errors?.gender : serverErrors?.gender
                      }
                      classes='my-2 f-13'
                    />
                  </div>
                </div>

                {/* دکمه ورود */}
                <div className='col-12 col-md-8'>
                  <div className='form-group d-flex flex-column'>
                    <button
                      type='submit'
                      className='btn btn-info rounded-1 my-2'
                      disabled={isSubmitting}
                    >
                      {formLoading ? (
                        <div
                          className='spinner-border spinner-border-sm text-white'
                          role='status'
                        />
                      ) : (
                        'ثبت نام'
                      )}
                    </button>
                  </div>
                </div>
                <div className='col-12 col-md-4'>
                  <div className='form-group d-flex flex-column'>
                    <Link
                      to='/login'
                      className='btn btn-secondary rounded-1 my-2'
                    >
                      ورود
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default Register;
